import { useEffect } from 'react';

export interface UseColumnSettingsObserverOptions {
  tableRef: React.RefObject<HTMLDivElement>;
  updateColumnSettings: () => void;
  checkHorizontalScroll?: () => void;
  dependencies?: any[];
}

export const useColumnSettingsObserver = (
  options: UseColumnSettingsObserverOptions
): void => {
  const {
    tableRef,
    updateColumnSettings,
    checkHorizontalScroll,
    dependencies = []
  } = options;

  useEffect(() => {
    const handleResize = () => {
      checkHorizontalScroll?.();
    };

    const timer = setTimeout(() => {
      checkHorizontalScroll?.();
    }, 50);

    window.addEventListener('resize', handleResize);

    const container = tableRef.current;
    if (container) {
      container.addEventListener('scroll', () => {
        checkHorizontalScroll?.();
      });

      const handleColumnReorder = () => {
        setTimeout(() => {
          updateColumnSettings();
        }, 100);
      };

      const handleDragEnd = (e: DragEvent) => {
        if (
          (e.target as HTMLElement)?.tagName === 'TH' ||
          (e.target as HTMLElement)?.closest('th')
        ) {
          handleColumnReorder();
        }
      };

      const handleMouseUp = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (
          target?.closest('th') ||
          target?.classList.contains('p-column-resizer')
        ) {
          setTimeout(() => {
            updateColumnSettings();
          }, 100);
        }
      };

      const observer = new MutationObserver((mutations) => {
        checkHorizontalScroll?.();
        const hasColumnReorder = mutations.some((mutation) => {
          return (
            Array.from(mutation.addedNodes).some(
              (node) => node instanceof HTMLElement && node.tagName === 'TH'
            ) ||
            Array.from(mutation.removedNodes).some(
              (node) => node instanceof HTMLElement && node.tagName === 'TH'
            )
          );
        });
        if (hasColumnReorder) {
          handleColumnReorder();
        }
      });

      container.addEventListener('dragend', handleDragEnd);
      container.addEventListener('mouseup', handleMouseUp);

      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });

      return () => {
        window.removeEventListener('resize', handleResize);
        container.removeEventListener('scroll', () => {
          checkHorizontalScroll?.();
        });
        container.removeEventListener('dragend', handleDragEnd);
        container.removeEventListener('mouseup', handleMouseUp);
        observer.disconnect();
        clearTimeout(timer);
      };
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, [tableRef, updateColumnSettings, checkHorizontalScroll, ...dependencies]);
};

